// app/features/ai-workflows/components/RestartUseCaseButtonComponent.tsx
// app/features/personalized-use-cases/components/RestartUseCaseButtonComponent.tsx
"use client";

import { RotateCcw } from "lucide-react";

// Use your custom button
import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";

import { PersonalizedUseCase } from "../personalized-use-case.interface"; // Import the type
import { usePersonalizedUseCasesHook } from "../services_and_hooks/usePersonalizedUseCasesHook";

interface RestartUseCaseButtonProps {
  useCase: PersonalizedUseCase; // Expect the full use case object
  disabled?: boolean;
}

export const RestartUseCaseButtonComponent = ({
  useCase,
  disabled = false,
}: RestartUseCaseButtonProps) => {
  // This hook now contains the correctly wired-up restart logic
  const { restartUseCase, isRestartingUseCase, restartError } =
    usePersonalizedUseCasesHook();

  const handleRestart = async () => {
    try {
      // Just pass the job ID (use case ID) - backend auto-determines checkpoint
      await restartUseCase(useCase.id);
    } catch (error) {
      // Error is already handled by the mutation's onError toast, but you can log it here too.
      console.error("Component-level error during restart:", error);
    }
  };

  // Logic to determine if the button should be shown
  const isFailed = useCase.generation_status === "failed";
  if (!isFailed) {
    return null; // Only show for failed use cases
  }

  return (
    <div className="flex flex-col gap-3 p-4 rounded-lg bg-orange/5">
      <p className="text-sm font-medium text-orange-800">
        Something went wrong. You can restart the generation.
      </p>

      <HTFButton
        onClick={handleRestart}
        disabled={disabled || isRestartingUseCase}
        variant="secondary"
        size="sm"
        isLoading={isRestartingUseCase}
        icon={<RotateCcw size={16} />}
        className="bg-secondary-500/10 border-secondary-500 text-secondary-800 hover:text-black hover:bg-teal hover:border-secondary-500 focus:ring-secondary-500/30 transition-all duration-200"
        // className="bg-secondary-500/10 border-secondary-500/30 text-secondary-800 hover:bg-secondary-500/20 hover:border-secondary-500/50 focus:ring-secondary-500/30 transition-all duration-200"
        // className="bg-secondary-500/10 border-secondary-500/30 text-secondary-800 hover:bg-secondary-500/20 hover:border-secondary-500/50 focus:ring-secondary-500/30 transition-all duration-200"
        // className="bg-secondary-500/10 border-secondary-500/30 text-secondary-800 hover:bg-secondary-500/20 hover:border-secondary-500/50 focus:ring-secondary-500/30 transition-all duration-200"
      >
        {isRestartingUseCase ? "Restarting..." : "Restart Generation"}
      </HTFButton>

      {restartError && (
        <p className="text-xs text-error-dark">Error: {restartError.message}</p>
      )}
    </div>
  );
};
